// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, xScreen } from "@stanion/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: { [fields.links]: LinkModel[] };
    extendedStyles?: FooterLinkListStyles;
}

export interface FooterLinkListStyles {
    footerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const footerLinkListStyles: FooterLinkListStyles = {
    footerLinkListWrapper: {
        css: css`
            text-align: right;
            display: flex;
            flex-shrink: 0;
            > a:not(:last-child) {
                margin-right: 12px;
                padding-right: 12px;
                border-right: 1px solid ${color("secondary")};
            }
            ${xScreen("gte", "sm")} {
                > a:first-child {
                    padding-left: 12px;
                    border-left: 1px solid ${color("secondary")};
                }
            }
        `,
    },
    link: {
        typographyProps: {
            size: 12,
            lineHeight: "13px",
            css: css`
                margin: 0;
                letter-spacing: 0;
                display: inline-block;
                white-space: nowrap;
            `,
        },
    },
};

const styles = footerLinkListStyles;

export const FooterLinkList: React.FC<OwnProps> = ({ fields }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);

    if (links.length < 1) {
        return null;
    }

    return (
        <StyledWrapper {...styles.footerLinkListWrapper}>
            {links.map(
                (link, index) =>
                    link.url && (
                        <Link
                            key={`${link.url}.${link.title}`}
                            href={link.url}
                            target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                            {...styles.link}
                        >
                            {fields.links[index].fields.overriddenTitle || link.title}
                        </Link>
                    ),
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterLinkList,
    definition: {
        group: "Footer",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
